import { Chip, IconButton, Stack, Typography } from '@mui/material';
import type { MeterInput } from '@vy/types';
import { MeterInputSchema } from '@vy/types';
import {
  isEmptyObject,
  isEmptyString,
  isNotEmptyObject,
  isNotNullOrUndefined,
  isNullOrUndefined,
  vySerialize,
  withDefault,
} from '@vy/utils';
import { get } from 'lodash';
import React from 'react';
import type { UseFormSetFocus } from 'react-hook-form';
import { useFormState } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { CloseIcon } from '../../../../components/icons';
import { AiIcon } from '../../../../components/icons/AiIcon';
import { useProductDescription } from '../../../../hooks/requests/use-product-description';
import { useDebounce } from '../../../../hooks/use-debounce';

export const ProductDescription: React.FC<{ toggleDescriptionVisibility: () => void }> = ({
  toggleDescriptionVisibility,
}) => {
  const { control, setFocus, setValue } = useFormContext<MeterInput>();
  const { errors, dirtyFields } = useFormState({ control });
  const meterInput = useWatch<MeterInput>();

  const debouncedMeterInput = useDebounce(vySerialize(meterInput));
  const parsedDebounceMeterInput: MeterInput = MeterInputSchema.safeParse(JSON.parse(debouncedMeterInput))
    .success
    ? MeterInputSchema.parse(JSON.parse(debouncedMeterInput))
    : {};
  const parsedDebounceMeterInputWithoutDescription = {
    ...parsedDebounceMeterInput,
    description: undefined,
  };

  const { data, isLoading } = useProductDescription(parsedDebounceMeterInputWithoutDescription, {
    enabled:
      isNotEmptyObject(dirtyFields) &&
      isEmptyObject(errors) &&
      MeterInputSchema.safeParse(meterInput).success,
    onSuccess: ({ description }) => {
      setValue('description', description);
    },
  });

  return (
    <div className="relative h-full w-full min-w-[354px] max-w-[354px] border-l border-l-[#DFEBF1] bg-white px-6 py-4">
      <div className="flex items-center justify-between text-center">
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          <Typography variant="h6">Product Description</Typography>
          {isNotNullOrUndefined(data) && <AiIcon />}
        </Stack>
        <IconButton onClick={() => toggleDescriptionVisibility()}>
          <CloseIcon color="#576063" />
        </IconButton>
      </div>
      {isNotEmptyObject(dirtyFields) &&
      (isLoading ||
        isNullOrUndefined(data) ||
        isEmptyString(data?.description) ||
        !MeterInputSchema.safeParse(meterInput).success) ? (
        <Typography
          variant="body2"
          noWrap
          sx={{
            color: "text.secondary",
            display: "flex",
            columnGap: 1,
            alignItems: "center",
            marginTop: 2
          }}>
          <AiIcon />
          waiting...
        </Typography>
      ) : (
        <div className="mt-4">
          {isNotEmptyObject(dirtyFields)
            ? renderTemplate(data?.description ?? '', MeterInputSchema.parse(meterInput), setFocus)
            : renderTemplate(
                withDefault(meterInput.description, ''),
                MeterInputSchema.parse(meterInput),
                setFocus,
              )}
        </div>
      )}
    </div>
  );
};

const renderTemplate = (template: string, meterInput: MeterInput, setFocus: UseFormSetFocus<MeterInput>) => {
  const parts = template.split(/({{.*?}})/g);

  return parts.map((part, index) => {
    const match = part.match(/{{(.*?)}}/);

    if (match) {
      const key = match[1].trim();
      const value = withDefault(get(meterInput.filter, key), get(meterInput, key));

      if (isNullOrUndefined(value)) {
        return 'MISSING';
      }

      if (key === ('eventName' satisfies keyof MeterInput)) {
        return (
          <Chip
            key={index}
            label={value}
            color="primary"
            variant="outlined"
            onClick={() => {
              setFocus('eventName');
            }}
          />
        );
      } else if (key.endsWith('operator')) {
        return (
          <Typography key={index} variant="body2" sx={{
            display: "inline"
          }}>
            {value.toLowerCase()}
          </Typography>
        );
      } else {
        return (
          <Chip
            key={index}
            label={value}
            color="secondary"
            variant="outlined"
            onClick={() => {
              setFocus(key as keyof MeterInput);
            }}
          />
        );
      }
    } else {
      return (
        <Typography
          key={index}
          variant="body2"
          sx={{
            display: "inline",
            lineHeight: 2.75
          }}>
          {part}
        </Typography>
      );
    }
  });
};
